import { createContext, useContext, type ComponentType, type ReactNode, type AnchorHTMLAttributes } from "react"

/**
 * Router-agnostic navigation adapter for @edemics/components.
 *
 * Legacy EdemicsAllModule imports next/link + next/navigation directly.
 * The extracted apps (tenant-app, super-admin) run on Vite + react-router,
 * so shared components can't hardcode either framework. Each app wraps its
 * tree in <RouterAdapterProvider> once, feeding it the router primitives it
 * uses, and every consumer of the sidebar/header/etc. picks them up via
 * useRouterAdapter().
 *
 * Contract for the injected Link component: MUST accept `href` (not `to`),
 * standard <a> attributes, and render children. Consumers wrap their
 * router's Link if the prop names differ (see apps/[app]/app/Providers.tsx).
 */

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  children?: ReactNode
}

export type LinkComponent = ComponentType<LinkProps>

export interface RouterAdapter {
  Link: LinkComponent
  usePathname: () => string
}

const RouterAdapterContext = createContext<RouterAdapter | null>(null)

export interface RouterAdapterProviderProps {
  adapter: RouterAdapter
  children: ReactNode
}

export function RouterAdapterProvider({ adapter, children }: RouterAdapterProviderProps) {
  return <RouterAdapterContext.Provider value={adapter}>{children}</RouterAdapterContext.Provider>
}

/**
 * Hook used inside shared components. Throws with a clear message if the
 * app forgot to mount the provider — otherwise the component would render
 * with a broken Link and silently strand navigation.
 */
export function useRouterAdapter(): RouterAdapter {
  const ctx = useContext(RouterAdapterContext)
  if (!ctx) {
    throw new Error(
      "RouterAdapterProvider missing. Wrap your app tree with <RouterAdapterProvider adapter={{ Link, usePathname }}> — see apps/tenant-app/src/app/Providers.tsx.",
    )
  }
  return ctx
}
